import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment, StockageService } from '@perval/shared';
import { Amplify } from 'aws-amplify';
import {
  confirmUserAttribute,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
  signIn,
  signInWithRedirect,
  signOut,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { SharedStateLegacyService } from '../../../shared/services/stockage/shared-state-legacy.service';
import { Nullable } from '../../../shared/types';

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  constructor(
    private readonly stockageService: StockageService,
    private readonly sharedStateLegacyService: SharedStateLegacyService,
    private readonly router: Router,
  ) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: environment.cognito.userPoolId,
          userPoolClientId: environment.cognito.userPoolWebClientId,
          loginWith: {
            oauth: {
              domain: environment.cognito.oauth.domain,
              scopes: environment.cognito.oauth.scope,
              redirectSignIn: [environment.cognito.oauth.redirectSignIn],
              redirectSignOut: [environment.cognito.oauth.redirectSignOut],
              responseType: environment.cognito.oauth.responseType as
                | 'code'
                | 'token',
            },
          },
        },
      },
    });
  }

  public async signIn(username: string, password: string): Promise<void> {
    try {
      await signIn({
        // On retire les espaces pour éviter des erreurs d'authentification
        username: username.trim().toLowerCase(),
        password,
      });

      await this.setTokensInSession('Extra.Not');
      // TODO : gérer les erreurs correctement dans NPER-812
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('error signing in', error);
    }
  }

  public async signOut(): Promise<void> {
    await signOut();
    this.stockageService.clear();
    this.sharedStateLegacyService.clear();
    await this.router.navigate(['connexion']);
  }

  public async idNotSignIn(): Promise<void> {
    await signInWithRedirect({
      provider: {
        custom: 'ID.Not',
      },
    });
  }

  public setTokensInSession(provider: string): void {
    this.stockageService.setItem('Provider', provider);
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      const user = await getCurrentUser();
      return !!user;
    } catch {
      return false;
    }
  }

  public async isSessionValid(): Promise<boolean> {
    const currentUser = await this.getUser();
    if (currentUser) {
      const session = await fetchAuthSession();
      return !!session;
    }

    return false;
  }

  public async getUser(): Promise<FetchUserAttributesOutput> {
    return await fetchUserAttributes();
  }

  public async getCurrentLogin(): Promise<Nullable<string>> {
    try {
      const provider = this.stockageService.getItem('Provider');
      let currentUserUsername: string = '';

      if (provider == 'Extra.Not') {
        currentUserUsername = (await getCurrentUser()).username as string;
      } else if (provider == 'ID.Not') {
        const user = await this.getUser();
        currentUserUsername = (user.preferred_username as string) || '';
      }

      return currentUserUsername;
    } catch {
      return null;
    }
  }

  public async changeEmail(email: string): Promise<void> {
    await updateUserAttributes({ userAttributes: { email } });
  }

  public async validateCodeVerification(
    confirmationCode: string,
  ): Promise<string> {
    try {
      await confirmUserAttribute({
        userAttributeKey: 'email',
        confirmationCode,
      });

      return Promise.resolve('SUCCESS');
      // TODO : gérer les erreurs correctement dans NPER-812
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }
}
